/**
 * One rule for "are these two spellings the same word", shared by everything that has to decide.
 *
 * It lived in `lexicon.ts` and was duplicated by hand in `engine.ts`, which is how the personal-count
 * bug got in: the lexicon folded case for *matching* but the learning path keyed its n-grams on the raw
 * token, so a word typed on the keyboard and the same word in the corpus were two different words. The
 * row then offered `DAS`, `Das` and `das` in three of four slots while the personal counts earned by
 * typing scored nothing at all.
 *
 * So the fold gets its own module. `lexicon.ts` re-exports it under its original name, and there is
 * exactly one definition for the lookup, the scorer, the learner and the candidate de-duplication to
 * agree on.
 */

/**
 * Normalisation for matching, applied to both sides.
 *
 * Case-insensitive, because the person types `w` and means `Wasser` — German capitalises nouns and
 * making them hunt for a shift they do not have would be absurd. The stored casing is what comes back
 * out, so `Wasser` is suggested with its capital intact.
 *
 * `ae`/`oe`/`ue` fold to the umlaut so a mistype still matches (§5.3). Deliberately one-directional:
 * typing `ue` finds `über`, and typing `ü` also finds it, but a word genuinely containing `ue`
 * (`Quelle`) is unaffected because the fold applies to the *search key* on both sides equally.
 */
export function normaliseForMatch(text: string): string {
  return text
    .toLowerCase()
    .replace(/ae/g, 'ä')
    .replace(/oe/g, 'ö')
    .replace(/ue/g, 'ü');
}

/** `wallis` → `Wallis`. First character only; the rest is left exactly as given. */
export const capitaliseFirst = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/** Whether two spellings differ by case and nothing else. `ueber`/`über` do **not** qualify. */
export const sameButForCase = (a: string, b: string): boolean =>
  a !== b && a.toLowerCase() === b.toLowerCase();
